import React, { useEffect, useRef, useState } from "react";
import { ProductRow } from "../../types";
import {
  cn,
  normalizeProductKey,
} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";

interface Props {
  index: number;
  row: ProductRow;
  rows: ProductRow[];
  setRows: (rows: ProductRow[]) => void;
  searchInputs: string[];
  setSearchInputs: (i: string[]) => void;
  categoryProducts: string[];
  showLastYear: boolean;
  setShowLastYear: (v: boolean) => void;
  onProductChange: (rowIndex: number, product: string) => void;
  onVolumeChange: (rowIndex: number, volume: string) => void;
  onLastYearVolumeChange?: (rowIndex: number, value: number | null) => void;
  onRowErrorStateChange: (index: number, hasError: boolean) => void;
  lastYearVolumesByProduct: Record<string, number>;
  takenProducts?: string[];
  disabled?: boolean;
}

const formatNumberForDisplay = (value: string) => {
  if (!value) return "";
  const withSpaces = value.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${withSpaces} kg`;
};

const ProductRowItem: React.FC<Props> = ({
  index,
  row,
  rows,
  setRows,
  searchInputs,
  setSearchInputs,
  categoryProducts,
  showLastYear,
  setShowLastYear,
  onProductChange,
  onVolumeChange,
  onRowErrorStateChange,
  lastYearVolumesByProduct,
  onLastYearVolumeChange,
  takenProducts = [],
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState<number>(0);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (rowRef.current && !rowRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const hasLastYearForProduct = (product: string): boolean => {
    const key = normalizeProductKey(product);
    const v = lastYearVolumesByProduct[key];
    return typeof v === "number" && v > 0;
  };

  const filteredProducts = (() => {
    const input = (searchInputs[index] ?? "").toLowerCase();
    const base =
      input.length < 2
        ? categoryProducts
        : categoryProducts.filter((p) => p.toLowerCase().includes(input));

    return [...base].sort((a, b) => {
      const aHas = hasLastYearForProduct(a) ? 1 : 0;
      const bHas = hasLastYearForProduct(b) ? 1 : 0;
      return bHas - aHas;
    });
  })();

  const isTaken = (product: string) =>
    takenProducts.includes(normalizeProductKey(product));

  const findAutoSelectProduct = (raw: string): string | null => {
    const typed = normalizeProductKey(raw);
    if (!typed) return null;

    const exact = categoryProducts.find(
      (p) => normalizeProductKey(p) === typed
    );
    if (exact) return exact;

    const matches = categoryProducts.filter((p) =>
      p.toLowerCase().includes(typed)
    );

    return matches.length === 1 ? matches[0] : null;
  };

  const clearSelectedProduct = () => {
    if (!row.product && row.lastYearVolume == null) return;

    const updated = [...rows];
    updated[index] = {
      ...updated[index],
      product: "",
      lastYearVolume: null,
    };

    setRows(updated);
    onProductChange(index, "");
    onLastYearVolumeChange?.(index, null);
  };

  const handleProductSelect = async (product: string) => {
    if (isTaken(product)) return;

    const updated = [...rows];
    updated[index].product = product;

    const key = normalizeProductKey(product);
    const lastYearVolume =
      typeof lastYearVolumesByProduct[key] === "number"
        ? lastYearVolumesByProduct[key]
        : null;

    updated[index].lastYearVolume = lastYearVolume;
    setRows(updated);

    const si = [...searchInputs];
    si[index] = product;
    setSearchInputs(si);

    onProductChange(index, product);
    onLastYearVolumeChange?.(index, lastYearVolume);

    setShowLastYear(true);
    setOpen(false);
  };

  const handleVolumeChangeLocal = (rawValue: string) => {
    const numeric = rawValue.replace(/[^\d]/g, "");

    const updated = [...rows];
    updated[index].volume = numeric;
    setRows(updated);

    onVolumeChange(index, numeric);
  };

  useEffect(() => {
    const hasError = !!row.product && !row.volume && touched && !isFocused;

    onRowErrorStateChange(index, hasError);
  }, [row.product, row.volume, touched, isFocused]);

  return (
    <div ref={rowRef} className="relative flex">
      <div className="relative w-full border-r border-disabled-bg">
        <input
          type="text"
          id={`product-${index}`}
          value={searchInputs[index]}
          disabled={disabled}
          onChange={(e) => {
            if (disabled) return;
            const value = e.target.value;
            const si = [...searchInputs];
            si[index] = value;
            setSearchInputs(si);

            // If user edits input and it no longer matches selected product -> clear selection (prevents "looks selected but isn't" and "was selected previously" bugs)
            if (row.product) {
              const typed = normalizeProductKey(value);
              const selected = normalizeProductKey(row.product);

              if (typed !== selected) {
                clearSelectedProduct();
              }
            }

            if (value.length >= 1) {
              setOpen(true);
              setHighlightIndex(0);
            } else {
              setOpen(false);
            }
          }}
          // Allow keyboard navigation in product list
          onKeyDown={(e) => {
            if (disabled) return;
            if (!open) return;

            if (open && filteredProducts.length === 0) {
              if (e.key === "Escape") setOpen(false);
              return;
            }

            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlightIndex((prev) =>
                Math.min(prev + 1, filteredProducts.length - 1)
              );
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlightIndex((prev) => Math.max(prev - 1, 0));
            }
            if (e.key === "Enter") {
              e.preventDefault();
              const product = filteredProducts[highlightIndex];
              if (product && !isTaken(product))
                void handleProductSelect(product);
            }
            if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          onFocus={() => {
            if (disabled) return;
            setOpen(true);
            setHighlightIndex(0);
          }}
          onClick={() => {
            if (disabled) return;
            setOpen(true);
            setHighlightIndex(0);
          }}
          onBlurCapture={() => {
            requestAnimationFrame(() => {
              setOpen(false);

              const value = searchInputs[index] ?? "";
              const auto = findAutoSelectProduct(value);
              const autoOk = auto && !isTaken(auto) ? auto : null;

              if (autoOk) {
                if (
                  row.product.trim().toLowerCase() !==
                  autoOk.trim().toLowerCase()
                ) {
                  void handleProductSelect(autoOk);
                }
                return;
              }

              if ((value ?? "").trim() !== "") {
                clearSelectedProduct();
              }
            });
          }}
          autoComplete="off"
          aria-autocomplete="none"
          className={cn(
            "w-full py-3 pl-2 pr-6 border-l border-disabled-bg h-[54px] tablet:pl-3 tablet:pr-10",
            !row.product && (searchInputs[index] ?? "").trim() && "text-black",
            disabled && "cursor-not-allowed"
          )}
        />

        {/* Product not set -> Display chevron icon, and show product list on click
        Product set -> Display X icon, and remove set product on click */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            if (row.product) {
              const updatedRows = [...rows];
              updatedRows[index] = {
                ...updatedRows[index],
                product: "",
                volume: "",
                lastYearVolume: null,
              };
              setRows(updatedRows);

              const si = [...searchInputs];
              si[index] = "";
              setSearchInputs(si);

              onProductChange(index, "");
              onVolumeChange(index, "");
              onLastYearVolumeChange?.(index, null);

              setOpen(false);
            } else {
              setOpen((o) => !o);
              setHighlightIndex(0);
            }
          }}
          className="absolute top-0 right-0 flex items-center justify-center h-full w-[30px] tablet:w-[54px]"
        >
          <span
            className={`!text-3xl !leading-none ${
              row.product
                ? "icon-x"
                : open
                ? "icon-chevron-up"
                : "icon-chevron-down"
            }`}
          />
        </button>

        {/* Product(s) dropdown list */}
        {open && (
          <ul
            className="absolute z-20 w-full overflow-y-auto bg-white border shadow-lg border-disabled-bg max-h-72"
            onMouseDown={(e) => {
              e.preventDefault();
            }}
          >
            {filteredProducts.length > 0 ? (
              filteredProducts.map((product, i) => {
                const disabled = isTaken(product);
                return (
                  <li
                    key={product}
                    onMouseEnter={() => !disabled && setHighlightIndex(i)}
                    onClick={() => {
                      if (disabled) return;
                      void handleProductSelect(product);
                    }}
                    aria-disabled={disabled}
                    title={
                      disabled
                        ? "Dette produktet er allerede valgt i en annen rad."
                        : undefined
                    }
                    className={cn(
                      "px-4 py-2 flex items-center justify-between gap-3",
                      disabled
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer",
                      !disabled && i === highlightIndex && "bg-yellow-25"
                    )}
                  >
                    <span className="truncate">{product}</span>

                    {hasLastYearForProduct(product) && (
                      <span
                        className={cn(
                          "icon-chart-area !text-sm !leading-none shrink-0",
                          "text-black-subtile"
                        )}
                        aria-hidden="true"
                        title="Finnes i tidligere rapport"
                      />
                    )}
                  </li>
                );
              })
            ) : (
              <li className="px-4 py-2 text-black-subtile cursor-default select-none">
                Ingen treff
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Volume */}
      <div className="relative w-full">
        <input
          disabled={disabled || !row.product}
          type="text"
          id="volume"
          required
          value={
            isFocused
              ? row.volume.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
              : row.volume
              ? formatNumberForDisplay(row.volume)
              : ""
          }
          onChange={(e) => {
            if (disabled) return;
            handleVolumeChangeLocal(e.target.value);
          }}
          onFocus={() => {
            if (disabled) return;
            setIsFocused(true);
            setTouched(true);
          }}
          onBlur={() => {
            setIsFocused(false);
            if (!row.product) setTouched(false);
          }}
          autoComplete="off"
          aria-autocomplete="none"
          className={cn(
            "w-full px-2 py-3 h-[54px] tablet:px-3",
            touched &&
              !isFocused &&
              row.product &&
              !row.volume &&
              "placeholder-red-secondary",
            touched && !isFocused && row.product && !row.volume
              ? "border-red-secondary border-2"
              : "border-r border-disabled-bg",
            (disabled || !row.product) && "cursor-not-allowed"
          )}
          placeholder={
            disabled
              ? "Velg produktkategori først"
              : touched && !isFocused && row.product && !row.volume
              ? "Dette feltet mangler *"
              : undefined
          }
        />
      </div>
      {/* Last year's volume */}
      {showLastYear && (
        <div className="w-full max-w-[70px] tablet:max-w-[200px]">
          <p className="w-full px-2 py-3 border-r border-disabled-bg h-[54px] tablet:px-3">
            {row.lastYearVolume !== null
              ? formatNumberForDisplay(String(row.lastYearVolume))
              : "—"}
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductRowItem;
